import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { trackEvent } from '../utils/analytics';

const CartContext = createContext(null);

// A cart "line" must be unique per product + variant + customizations.
// Two items with the same id/variant but different personalisation
// (e.g. different name/charm entered) are NOT the same line and must
// not be merged together.
const buildCartItemId = (id, variant, customizations_payload = []) => {
  const customKey = (customizations_payload || [])
    .slice()
    .sort((a, b) => String(a.field_id).localeCompare(String(b.field_id)))
    .map(cf => `${cf.field_id}:${Array.isArray(cf.value) ? cf.value.join(',') : cf.value}`)
    .join('|');
  return `${id}::${variant}::${customKey}`;
};

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existing = state.items.find(i => i.cartItemId === action.payload.cartItemId);
      if (existing) {
        return {
          ...state,
          items: state.items.map(i =>
            i.cartItemId === action.payload.cartItemId
              ? { ...i, qty: i.qty + (action.payload.qty || 1) }
              : i
          ),
        };
      }
      // giftWrap comes from the payload (set by addItem based on PDP selection)
      return {
        ...state,
        items: [...state.items, { ...action.payload, qty: action.payload.qty || 1 }],
      };
    }
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(i => i.cartItemId !== action.payload.cartItemId),
      };
    case 'UPDATE_QTY':
      if (action.payload.qty < 1) {
        return {
          ...state,
          items: state.items.filter(i => i.cartItemId !== action.payload.cartItemId),
        };
      }
      return {
        ...state,
        items: state.items.map(i =>
          i.cartItemId === action.payload.cartItemId
            ? { ...i, qty: action.payload.qty }
            : i
        ),
      };
    // Toggle gift wrapping on/off for a specific cart item
    case 'TOGGLE_GIFT_WRAP':
      return {
        ...state,
        items: state.items.map(i =>
          i.cartItemId === action.payload.cartItemId
            ? { ...i, giftWrap: !i.giftWrap }
            : i
        ),
      };
    case 'CLEAR_CART':
      return { ...state, items: [] };
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });

  // customizations_payload — pre-built array from ProductDetail:
  //   [{ field_id, field_name, field_label, field_type, value }]
  // selectedGiftWrapAddon — addon object if user ticked gift wrap, else null.
  const addItem = useCallback((product, variant, qty = 1, customizations_payload = [], customizationFields = [], selectedGiftWrapAddon = null) => {
    // Ensure it's a clean array (filter out any blanks)
    const customizations_payload_clean = Array.isArray(customizations_payload)
      ? customizations_payload.filter(cf => cf && cf.value !== undefined && cf.value !== '')
      : [];

    // variant may be "Color / Size" (combined) or a single value.
    // Split and resolve each part independently against product_variants.
    const norm = (s) => (s ?? '').toString().trim().toLowerCase();
    const variantParts = variant.split(' / ').map(s => s.trim());

    const selected_variant_records = variantParts
      .map(part =>
        (product.product_variants || []).find(
          v => norm(v.variant_value) === norm(part) || norm(v.variant_name) === norm(part)
        ) ?? null
      )
      .filter(Boolean);

    // Keep first record for backward compat (used by checkout for single-variant products)
    const selected_variant_record = selected_variant_records[0] ?? null;

    // ── Variant-wise pricing ──────────────────────────────────────────────
    // Selling price = discount_price if set, else base_price (falls back to
    // product.price for already-normalised product objects).
    // Each matched variant record can carry its own price_adjustment
    // (e.g. Size "4'6\"" = +₹100 over the base "3'6\"" price) — sum every
    // selected variant's adjustment and add it on top of the base price to
    // get this line's true per-unit price.
    const basePrice = Number(
      product.discount_price ?? product.base_price ?? product.price ?? 0
    );
    const baseOriginalPrice = Number(product.base_price ?? product.price ?? basePrice);
    const variantPriceAdjustment = selected_variant_records.reduce(
      (sum, r) => sum + Number(r.price_adjustment || 0),
      0
    );

    // ── Customization-wise pricing ──────────────────────────────────────
    // select/radio/checkbox customization fields can carry a price_adjustment
    // per option (e.g. Sticker Theme "Super Mario" = +₹50). Stamp each
    // customization line with its own price_adjustment (summed across all
    // chosen values for multi-select checkbox fields) so Cart/Checkout/Order
    // screens can show a breakdown without re-deriving it from field defs.
    const customizations_payload_priced = customizations_payload_clean.map((cf) => {
      const field = (customizationFields || []).find((f) => f.id === cf.field_id);
      if (!field || !['select', 'radio', 'checkbox'].includes(field.field_type)) {
        return { ...cf, price_adjustment: 0 };
      }
      const options = field.customization_field_options || [];
      const values = Array.isArray(cf.value) ? cf.value : [cf.value];
      const price_adjustment = values.reduce(
        (s, val) => s + Number(options.find((o) => o.option_value === val)?.price_adjustment || 0),
        0
      );
      return { ...cf, price_adjustment };
    });
    const customizationPriceAdjustment = customizations_payload_priced.reduce(
      (sum, cf) => sum + Number(cf.price_adjustment || 0),
      0
    );

    const unitPrice = basePrice + variantPriceAdjustment + customizationPriceAdjustment;
    const unitOriginalPrice = baseOriginalPrice + variantPriceAdjustment + customizationPriceAdjustment;

    // Resolve the image to show in Cart/Checkout for this line item.
    // Priority: an image tagged with BOTH the chosen color and the chosen
    // customization option > color-only tagged image > customization-only
    // tagged image > the product's default/primary image.
    // (see normaliseProduct in api/index.js for how media gets variantId /
    // customOptionId stamped on it)
    const isColorType = (v) => {
      const t = v?.product_variant_type || v?.variant_type;
      const key = (t?.slug || t?.name || '').toString().toLowerCase();
      return key === 'color';
    };
    const colorVariantRecord = selected_variant_records.find(isColorType) ?? null;
    const media = product.media || [];

    // Resolve the matched customization option's id from any select/radio field
    const matchedOptionId = (() => {
      for (const cf of customizations_payload_clean) {
        const field = (product.customization_fields || []).find(f => f.id === cf.field_id);
        if (!field || (field.field_type !== 'select' && field.field_type !== 'radio')) continue;
        const opt = (field.customization_field_options || []).find(o => o.option_value === cf.value);
        if (opt) return opt.id;
      }
      return null;
    })();

    const bothMatch = colorVariantRecord && matchedOptionId
      ? media.find(m => m.variantId === colorVariantRecord.id && m.customOptionId === matchedOptionId)
      : null;
    const colorMatch = colorVariantRecord
      ? media.find(m => m.variantId === colorVariantRecord.id && !m.customOptionId)
      : null;
    const optionMatch = matchedOptionId
      ? media.find(m => m.customOptionId === matchedOptionId && !m.variantId)
      : null;

    const resolvedImage =
      bothMatch?.url || colorMatch?.url || optionMatch?.url ||
      product.images?.[0] || media[0]?.url || null;

    const cartItemId = buildCartItemId(product.id, variant, customizations_payload_clean);

    dispatch({
      type: 'ADD_ITEM',
      payload: {
        ...product,
        cartItemId,                       // ← unique per product + variant + customizations
        variant,
        qty,
        image: resolvedImage,             // ← color-variant-aware image for Cart/Checkout display
        price: unitPrice,                 // ← overrides product.price: base price + variant + customization adjustment(s)
        originalPrice: unitOriginalPrice, // ← compare-at price, also adjustment-inclusive (for strike-through display)
        basePrice,                        // ← selling price before any variant/customization adjustment (for breakdown display)
        variantPriceAdjustment,           // ← total ₹ added by the selected variant(s)
        customizationPriceAdjustment,     // ← total ₹ added by priced customization option(s)
        customizations_payload: customizations_payload_priced, // ← each line now also carries its own price_adjustment
        selected_variant_record,          // ← first record, backward compat
        selected_variant_records,         // ← all resolved records (color + size, etc.)
        giftWrap: Boolean(selectedGiftWrapAddon),
        giftWrapPrice: selectedGiftWrapAddon
          ? Number(selectedGiftWrapAddon.product_addons?.custom_price ?? selectedGiftWrapAddon.price ?? 0)
          : 0,
      },
    });
    trackEvent('ADD_TO_CART', {
      product_id: product.id,
      metadata: { variant, qty, unit_price: unitPrice },
    });
  }, []);

  const removeItem = useCallback((cartItemId) => {
    const item = state.items.find((i) => i.cartItemId === cartItemId);
    dispatch({ type: 'REMOVE_ITEM', payload: { cartItemId } });
    if (item) trackEvent('REMOVE_FROM_CART', { product_id: item.id, metadata: { variant: item.variant } });
  }, [state.items]);

  const updateQty = useCallback((cartItemId, qty) => {
    dispatch({ type: 'UPDATE_QTY', payload: { cartItemId, qty } });
  }, []);

  const toggleGiftWrap = useCallback((cartItemId) => {
    dispatch({ type: 'TOGGLE_GIFT_WRAP', payload: { cartItemId } });
  }, []);

  const clearCart = useCallback(() => dispatch({ type: 'CLEAR_CART' }), []);

  const totalItems = state.items.reduce((sum, i) => sum + i.qty, 0);
  const subtotal = state.items.reduce((sum, i) => sum + i.price * i.qty, 0);

  // Sum gift wrap prices only for items where the user has toggled it on
  const giftWrapTotal = state.items.reduce((sum, i) => {
    if (!i.giftWrap) return sum;
    const addon = (i.addons || []).find(
      a => a.slug === 'gift-wrapping' && a.product_addons?.is_enabled === true
    );
    if (!addon) return sum;
    return sum + Number(addon.product_addons?.custom_price ?? addon.price ?? 0) * i.qty;
  }, 0);

  // `total` here is items subtotal only; delivery is added at checkout.
  // Gift wrap total is exposed separately so both Cart and Checkout can include it.
  const total = subtotal;

  return (
    <CartContext.Provider value={{
      items: state.items,
      addItem,
      removeItem,
      updateQty,
      toggleGiftWrap,
      clearCart,
      totalItems,
      subtotal,
      giftWrapTotal,
      total,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};